/**
 * CelesteLeftPseudoBox Component
 *
 * Celeste skin left pseudo box with sky-blue gradient surface, cloud decorations,
 * and blue bottom accent. Displays player username with truncation.
 */

import { memo } from 'react';
import type { PseudoBoxProps } from '../basics/constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from '../basics/utils/utils';
import { CELESTE_PSEUDO_VIEWBOX } from './constants';

const CelesteLeftPseudoBox = memo(function CelesteLeftPseudoBox({
  pseudo,
  viewBox = CELESTE_PSEUDO_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - celeste left position"
      focusable="false"
    >
      <defs>
        {/* Dark bottom base */}
        <linearGradient
          id="clpDark"
          x1="4.62"
          y1="70.26"
          x2="302.02"
          y2="70.26"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>
        {/* Blue mid-layer accent */}
        <linearGradient
          id="clpBlue"
          x1="4.62"
          y1="60.61"
          x2="302.05"
          y2="60.61"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FACD6" />
          <stop offset="0.48" stopColor="#4584C7" />
          <stop offset="1" stopColor="#3B57A1" />
        </linearGradient>
        {/* Celeste surface gradient */}
        <linearGradient
          id="clpSurface"
          x1="153.23"
          y1="4.7"
          x2="153.23"
          y2="78.63"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        {/* Cloud gradients */}
        <linearGradient
          id="clpCloud1"
          x1="242.255"
          y1="70.77"
          x2="286.567"
          y2="52.18"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        <linearGradient
          id="clpCloud2"
          x1="137.17"
          y1="25.41"
          x2="28.77"
          y2="25.41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        <linearGradient
          id="clpCloud3"
          x1="23.02"
          y1="35.17"
          x2="4.62"
          y2="35.17"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#72BBFC" />
          <stop offset="0.48" stopColor="#A7DFFC" />
          <stop offset="1" stopColor="#E6FBFF" />
        </linearGradient>
        {/* Text clip path */}
        <clipPath id="clpTextClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Layer 1: Dark bottom base */}
      <path
        d="M302.02 77.0399C302.04 79.3799 301.24 81.6699 299.71 83.4399C299.7 83.4599 299.69 83.4699 299.68 83.4799C299.68 83.4799 295.91 88.4699 290.65 90.5899C284.22 93.1799 279.22 91.0799 271.44 90.9699C263.22 90.8599 260.07 93.0799 251.26 95.1399C238.53 98.1299 235.67 95.7099 199.77 95.1399C198.92 95.1299 190.75 95.1199 174.4 95.1099C163.74 95.0999 148.82 95.0899 133.67 95.1099C112.6 95.1399 91.5301 95.0999 70.4601 95.1099C59.8801 95.1099 49.3201 93.5599 38.7501 94.0499C30.0901 94.4499 22.9101 95.3699 15.3101 91.3599C15.2601 91.3299 15.2101 91.2999 15.1501 91.2699C8.60012 87.7499 4.62012 80.8099 4.62012 73.3599V47.9299C4.74012 47.9399 4.86012 47.9599 4.98012 47.9699C10.6801 48.6699 16.4901 49.3299 22.3901 49.9599C57.4901 53.6899 96.1001 56.0699 137.74 56.0999C199.09 56.1299 254.22 51.0299 301.7 43.8999C301.74 47.7499 301.78 51.5999 301.81 55.4499V55.4899C301.86 60.5999 301.91 65.7199 301.96 70.8299C301.98 72.8999 302 74.9699 302.02 77.0399Z"
        fill="url(#clpDark)"
      />

      {/* Layer 2: Blue mid-layer accent */}
      <path
        d="M302.05 70.69C302.05 70.69 301.99 70.78 301.96 70.83C301.43 71.62 300.68 72.68 299.68 73.83C299.68 73.83 295.91 78.82 290.65 80.94C284.22 83.53 279.22 81.43 271.44 81.32C263.22 81.21 260.07 83.43 251.26 85.5C238.53 88.48 235.67 86.06 199.77 85.5C198.92 85.48 190.75 85.48 174.4 85.46C163.74 85.45 148.82 85.44 133.67 85.46C112.6 85.49 91.5301 85.46 70.4601 85.46C59.8801 85.46 49.3201 83.91 38.7501 84.4C30.0901 84.8 22.9101 85.73 15.3101 81.71C10.0401 78.92 6.66012 74.87 4.62012 71.85V38.29C34.6201 41.96 67.4401 44.73 102.74 45.87C109.15 46.08 115.65 46.24 122.23 46.33C127.35 46.41 132.52 46.45 137.73 46.45C199.08 46.49 254.21 41.38 301.69 34.25C301.74 39.46 301.79 44.67 301.84 49.87C301.91 56.81 301.97 63.75 302.04 70.69H302.05Z"
        fill="url(#clpBlue)"
      />

      {/* Layer 3: Celeste surface */}
      <path
        d="M301.85 27.7999V54.1199C301.85 54.5799 301.84 55.0399 301.81 55.4899C301.1 68.4699 290.34 78.7799 277.18 78.7799H40.5001C26.8001 78.7799 14.8901 71.0999 8.85012 59.8099C6.91012 56.1899 5.57012 52.1999 4.98012 47.9699C4.74012 46.3299 4.62012 44.6599 4.62012 42.9599V27.7999C4.62012 14.1799 15.6601 3.13989 29.2801 3.13989H277.18C290.8 3.13989 301.85 14.1799 301.85 27.7999Z"
        fill="url(#clpSurface)"
      />

      {/* Cloud decoration - right */}
      <path
        d="M242.86 71.1499C241.17 67.1199 244.43 61.9099 250.13 59.5199C251.31 59.0199 252.51 58.6899 253.68 58.4999C252.56 55.0599 254.58 51.1199 258.4 49.5199C261.52 48.2099 264.92 48.8499 266.97 50.8899C267.79 50.2299 268.75 49.6499 269.82 49.1999C274.39 47.2799 279.22 48.4399 280.62 51.7799C280.87 52.3899 281 53.0199 281 53.6699C284.83 53.4599 288.22 55.0599 289.47 58.0299C291.19 62.1299 288.19 67.2999 282.77 69.5699C279.56 70.9199 276.22 70.9499 273.68 69.8999C272.27 71.3199 270.39 72.5999 268.17 73.5199C263.89 75.3199 259.55 75.3299 256.9 73.8199C256.69 73.9199 256.48 74.0099 256.27 74.0999C250.56 76.4899 244.56 75.1699 242.87 71.1399L242.86 71.1499Z"
        fill="url(#clpCloud1)"
      />

      {/* Cloud decoration - left */}
      <path
        d="M137.17 29.5099C137.17 37.1199 131 43.6299 122.24 46.3299C119.44 47.1999 116.37 47.6799 113.16 47.6799C109.42 47.6799 105.89 47.0399 102.74 45.8699C99.26 44.6199 96.26 42.7399 93.96 40.4299C89.78 42.1599 84.76 43.1699 79.34 43.1699C68.87 43.1699 59.83 39.3999 55.63 33.9599C55.11 33.9899 54.59 33.9999 54.06 33.9999C40.1 33.9999 28.77 26.0099 28.77 16.1499C28.77 11.0199 31.84 6.38989 36.75 3.13989H122.68C123.67 4.87989 124.22 6.78989 124.22 8.78989C124.22 10.2699 123.92 11.6999 123.37 13.0499C131.53 15.9599 137.17 22.2399 137.17 29.5099Z"
        fill="url(#clpCloud2)"
      />

      {/* Cloud decoration - small left */}
      <path
        d="M16.8401 42.5698C14.2501 43.4198 11.6501 43.2398 9.73012 42.2598C9.46012 42.4998 9.16012 42.7298 8.85012 42.9398C7.82012 43.6798 6.59012 44.2998 5.21012 44.7498C5.01012 44.8098 4.82012 44.8698 4.62012 44.9298V26.0998C5.02012 26.3498 5.37012 26.6698 5.68012 27.0298C6.36012 26.5598 7.15012 26.1698 8.00012 25.8898C11.6801 24.6798 15.3801 25.8798 16.2701 28.5698C16.4301 29.0598 16.4801 29.5598 16.4501 30.0598C19.0601 30.1298 21.3201 31.2298 22.3901 33.0998C22.5401 33.3798 22.6801 33.6798 22.7801 33.9898C23.8601 37.2898 21.2101 41.1298 16.8401 42.5698Z"
        fill="url(#clpCloud3)"
      />

      {/* Dynamic player pseudo text */}
      <g clipPath="url(#clpTextClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.LEFT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="start"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
});

export default CelesteLeftPseudoBox;
